import React from 'react';
import { useLocation } from 'react-router-dom';

import Modal from 'antd/lib/modal';
import once from 'lodash/once';

import { ItemsProvider } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import Loader from '@common/react/components/Core/LoadingProvider/Loader';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useSecureRoutesProviderContext } from '@common/react/components/Routes/SecureRoute/BaseSecureRouteWrapper';

import { SearchObject } from '@app/components/UI/SearchModal/index';
import getRootCheckAdminMenu from '@app/components/Layouts/DashboardLayout/useRootCheckAdminMenu';
import { dashboardMenu, DashboardMenuItem } from '@app/components/Routes/DashboardMenu';
import getAccessibleMenu from '@app/components/Layouts/DashboardLayout/getAccessibleMenu';
import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import SearchMenu from '@app/components/UI/SearchModal/SearchMenu';
import { User, UserRole } from '@app/objects/User';
import SecureRouteWrapper from '@app/components/Routes/SecureRoute/SecureRouteWrapper';

import '@app/scss/components/searchModal.scss';

export interface SearchModalContext {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const createSearchModalContext = once(() => React.createContext({} as SearchModalContext));

export const useSearchModalContext: () => SearchModalContext = () => React.useContext(createSearchModalContext());

const CustomInput: React.FC<any> = ({ open, ...props }) => {
	const ref = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		if (open) {
			setTimeout(() => ref.current?.focus(), 0);
		}
	}, [open]);

	return <input
		{...props}
		ref={ref}
	/>;
};

const SearchModal: React.FC = ({ children }) => {
	const { checkUserAccess, getUser } = useApplicationContext();

	if (!checkUserAccess([UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff])) return <>{children}</>;

	const location = useLocation();
	const [open, setOpen] = React.useState(false);
	const user = getUser<User>();
	const { canAccess } = useSecureRoutesProviderContext();
	const { checkFeatureAccess } = useCompanyFeatureProviderContext();
	const rootCheckAdminMenu = getRootCheckAdminMenu();
	const Context = createSearchModalContext();

	React.useEffect(() => {
		const keydown = (e: KeyboardEvent) => {
			if (e.code === 'KeyK' && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				setOpen(true);
			}
		};
		user && document.addEventListener('keydown', keydown);
		return () => {
			user && document.removeEventListener('keydown', keydown);
		};
	}, [user]);

	React.useEffect(() => {
		setOpen(false);
	}, [location]);

	const menuItems = React.useMemo(() => {
		if (!user) {
			return [];
		}

		const checkFeaturesAccess = (value: DashboardMenuItem) => {
			if (value.features && value.features.length > 0) {
				return checkFeatureAccess(value.features, !!value.anyFeature);
			}
			return true;
		};
		return user?.root ? (rootCheckAdminMenu || dashboardMenu)
			: getAccessibleMenu(
				checkUserAccess,
				rootCheckAdminMenu,
				checkFeaturesAccess,
				canAccess,
			);
	}, [rootCheckAdminMenu, user, checkUserAccess]);

	const CustomInputMemo = React.useMemo(() => {
		return (props) => <CustomInput {...props} open={open} />;
	}, [open]);

	return <Context.Provider value={{ open, setOpen }}>
		<Modal
			open={open}
			footer={null}
			zIndex={1200}
			closeIcon={null}
			width={700}
			className="search-modal"
			onCancel={() => setOpen(false)}
			destroyOnClose
		>
			<ItemsProvider<SearchObject>
				delay={200}
				loadRequest="searchType"
				type="searchType"
				filters={{
					text: '',
				}}
			>
				{({ state: { items, loading, filters }, actions: { handleChange } }) => <>
					<div className="search-modal-control">
						<SimpleSearchInput
							onChange={(evt) => {
								if (evt.currentTarget.value !== filters.text) {
									handleChange(evt);
								}
							}}
							otherInputProps={{
								// eslint-disable-next-line
								// @ts-ignore
								autocomplete: 'off',
							}}
							inputComponent={CustomInputMemo}
						/>
					</div>
					<div className="is-relative search-modal-inner">
						<SearchMenu
							open={open}
							loading={loading}
							search={filters.text}
							searchItems={items}
							items={menuItems}
						/>
						{loading && <div className="spin-container"><Loader /></div>}
					</div>
				</>}
			</ItemsProvider>
		</Modal>
		{children}
	</Context.Provider>;
};

// provide SecureRoutesProviderContext
const SearchModalWrapper = ({ children }) => {
	return <SecureRouteWrapper
		path="/"
		redirectPath=""
	>
		<SearchModal>
			{children}
		</SearchModal>
	</SecureRouteWrapper>;
};

export default SearchModalWrapper;
